import { useEffect, useMemo, useRef, useState } from 'react'

/**
 * Compact multi-select dropdown. Default OR semantics.
 * Pass ``matchMode`` + ``onMatchModeChange`` to show Any/All toggle (Snoop sources).
 * Pass ``followScope`` + ``onFollowScopeChange`` for New vs All follows scope (Snoop).
 */
export default function MultiSelectFilter({
  options = [],
  selected = [],
  onChange,
  emptyLabel = 'Filter…',
  selectedSingularPrefix = 'Selected',
  footerNoun = 'options',
  searchPlaceholder = 'Search…',
  matchMode = null,
  onMatchModeChange = null,
  followScope = null,
  onFollowScopeChange = null,
  footerExtra = null,
}) {
  const [open, setOpen] = useState(false)
  const [filterQuery, setFilterQuery] = useState('')
  const rootRef = useRef(null)
  const showMatchMode =
    typeof onMatchModeChange === 'function' &&
    (matchMode === 'or' || matchMode === 'and')
  const showFollowScope =
    typeof onFollowScopeChange === 'function' &&
    (followScope === 'new' || followScope === 'all')

  useEffect(() => {
    if (!open) return undefined
    function onDocMouseDown(event) {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false)
      }
    }
    function onKey(event) {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDocMouseDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const selectedSet = useMemo(() => new Set(selected), [selected])
  const visibleOptions = useMemo(() => {
    const q = filterQuery.trim().toLowerCase()
    if (!q) return options
    return options.filter((name) => name.toLowerCase().includes(q))
  }, [options, filterQuery])

  if (!options.length) return null

  function toggle(name) {
    const next = new Set(selectedSet)
    if (next.has(name)) next.delete(name)
    else next.add(name)
    onChange([...next].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' })))
  }

  function selectAllVisible() {
    const next = new Set(selectedSet)
    visibleOptions.forEach((name) => next.add(name))
    onChange([...next].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' })))
  }

  const modeLabel = matchMode === 'and' ? 'all' : 'any'
  const scopeLabel = followScope === 'new' ? 'new' : 'all'
  const label =
    selected.length === 0
      ? emptyLabel
      : selected.length === 1
        ? [
            `${selectedSingularPrefix}: ${selected[0]}`,
            showFollowScope ? scopeLabel : null,
          ]
            .filter(Boolean)
            .join(' · ')
        : [
            `${selected.length} ${footerNoun}`,
            showFollowScope ? scopeLabel : null,
            showMatchMode ? modeLabel : null,
          ]
            .filter(Boolean)
            .join(' · ')

  const widePanel = showMatchMode || showFollowScope

  return (
    <div className="relative inline-flex items-center gap-1.5" ref={rootRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={[
          'inline-flex max-w-full items-center gap-2 rounded-lg border bg-white px-3 py-2.5 text-sm outline-none',
          'hover:border-gray-300 focus:border-[var(--encore-blue)] focus:ring-2 focus:ring-blue-100',
          selected.length > 0
            ? 'border-blue-200 text-blue-800'
            : 'border-gray-200 text-gray-700',
        ].join(' ')}
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <span className="truncate">{label}</span>
        <span className="shrink-0 text-gray-400">{open ? '▴' : '▾'}</span>
      </button>
      {selected.length > 0 ? (
        <button
          type="button"
          onClick={() => onChange([])}
          className="shrink-0 rounded-md px-1.5 py-1 text-xs font-medium text-gray-500 hover:bg-gray-100 hover:text-gray-800"
          title="Clear filter"
          aria-label="Clear filter"
        >
          Clear
        </button>
      ) : null}
      {open ? (
        <div
          className={[
            'absolute left-0 top-full z-40 mt-1 rounded-xl border border-gray-200 bg-white shadow-lg',
            widePanel
              ? 'w-[min(20rem,calc(100vw-2rem))]'
              : 'w-[min(18rem,calc(100vw-2rem))]',
          ].join(' ')}
        >
          <div className="border-b border-gray-100 p-2">
            {showFollowScope ? (
              <div
                className="mb-2 flex rounded-lg border border-gray-200 bg-gray-50 p-0.5"
                role="group"
                aria-label="Follow list scope"
              >
                <button
                  type="button"
                  onClick={() => onFollowScopeChange('new')}
                  className={[
                    'flex-1 rounded-md px-2 py-1.5 text-xs font-semibold transition-colors',
                    followScope === 'new'
                      ? 'bg-white text-blue-800 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700',
                  ].join(' ')}
                  title="Match selected sources in New Follows Usernames (this week)"
                >
                  New follows
                </button>
                <button
                  type="button"
                  onClick={() => onFollowScopeChange('all')}
                  className={[
                    'flex-1 rounded-md px-2 py-1.5 text-xs font-semibold transition-colors',
                    followScope === 'all'
                      ? 'bg-white text-blue-800 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700',
                  ].join(' ')}
                  title="Match selected sources in All Follows Usernames (includes quiet weeks)"
                >
                  All follows
                </button>
              </div>
            ) : null}
            {showMatchMode ? (
              <div
                className="mb-2 flex rounded-lg border border-gray-200 bg-gray-50 p-0.5"
                role="group"
                aria-label="Match selected options"
              >
                <button
                  type="button"
                  onClick={() => onMatchModeChange('or')}
                  className={[
                    'flex-1 rounded-md px-2 py-1.5 text-xs font-semibold transition-colors',
                    matchMode === 'or'
                      ? 'bg-white text-blue-800 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700',
                  ].join(' ')}
                  title="Show rows matching any selected"
                >
                  Any (OR)
                </button>
                <button
                  type="button"
                  onClick={() => onMatchModeChange('and')}
                  className={[
                    'flex-1 rounded-md px-2 py-1.5 text-xs font-semibold transition-colors',
                    matchMode === 'and'
                      ? 'bg-white text-blue-800 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700',
                  ].join(' ')}
                  title="Show rows matching every selected"
                >
                  All (AND)
                </button>
              </div>
            ) : null}
            <input
              type="search"
              value={filterQuery}
              onChange={(event) => setFilterQuery(event.target.value)}
              placeholder={searchPlaceholder}
              className="w-full rounded-md border border-gray-200 px-2.5 py-1.5 text-sm outline-none focus:border-[var(--encore-blue)] focus:ring-2 focus:ring-blue-100"
              autoFocus
            />
            <div className="mt-2 flex items-center justify-between gap-2 text-xs">
              <button
                type="button"
                onClick={selectAllVisible}
                className="font-medium text-[var(--encore-blue)] hover:underline"
              >
                Select visible
              </button>
              <button
                type="button"
                onClick={() => onChange([])}
                disabled={selected.length === 0}
                className="font-medium text-gray-500 hover:underline disabled:opacity-40"
              >
                Clear
              </button>
            </div>
          </div>
          <ul
            className="max-h-64 overflow-y-auto py-1"
            role="listbox"
            aria-multiselectable="true"
          >
            {visibleOptions.length === 0 ? (
              <li className="px-3 py-2 text-sm text-gray-400">No matches</li>
            ) : (
              visibleOptions.map((name) => {
                const checked = selectedSet.has(name)
                return (
                  <li key={name} role="option" aria-selected={checked}>
                    <label className="flex cursor-pointer items-center gap-2.5 px-3 py-1.5 text-sm text-gray-800 hover:bg-blue-50/60">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggle(name)}
                        className="rounded border-gray-300 text-[var(--encore-blue)] focus:ring-blue-100"
                      />
                      <span className="min-w-0 truncate" title={name}>
                        {name}
                      </span>
                    </label>
                  </li>
                )
              })
            )}
          </ul>
          <div className="border-t border-gray-100 px-3 py-1.5 text-[11px] text-gray-400">
            {footerExtra != null
              ? footerExtra
              : showMatchMode || showFollowScope
                ? `${options.length} ${footerNoun} in this snapshot${
                    selected.length
                      ? ` · ${showFollowScope ? `${scopeLabel} follows` : ''}${
                          showFollowScope && showMatchMode && selected.length > 1 ? ' · ' : ''
                        }${
                          showMatchMode && selected.length > 1
                            ? `matching ${matchMode === 'and' ? 'all' : 'any'}`
                            : ''
                        }`
                      : ''
                  }`
                : `${options.length} ${footerNoun} · match any selected`}
          </div>
        </div>
      ) : null}
    </div>
  )
}
